import type { AxiosInstance } from 'axios';
import { atsApi, peopleApi, timeApi } from './api';
import { USER_API_TO_LUMI } from './moduleSwitcher';
import { anyAccessAnswers, anyAccessGeneration, anyAccessInflight, anyAccessKey } from './dataImportAccessCache';

// Who may import into which module — decided by the module's own API, asked the
// same way everywhere (ADR 0062: the hub holds no authority, it only asks).
//
// The descriptor endpoint sits behind `data_import.manage` in every module, so a
// GET on it is a clean yes/no without a new API. The sidebar entry, the hub's
// locked rows and the hub's own page all read their answer from here.

export type ImportModuleKey = 'people' | 'ats' | 'time';

export const IMPORT_MODULE_KEYS: readonly ImportModuleKey[] = ['people', 'ats', 'time'];

export interface ImportModuleApi {
  key: ImportModuleKey;
  api: AxiosInstance;
  importBase: string;
  exportBase: string;
}

/** The three module APIs the hub talks to, with their import and export base paths. */
export const IMPORT_MODULES: Record<ImportModuleKey, ImportModuleApi> = {
  people: { key: 'people', api: peopleApi, importBase: '/data-import', exportBase: '/data-export' },
  ats: { key: 'ats', api: atsApi, importBase: '/tenant/data-import', exportBase: '/tenant/data-export' },
  time: { key: 'time', api: timeApi, importBase: '/data-import', exportBase: '/data-export' },
};

/**
 * Four states, because a refusal and an unanswered probe are not the same fact
 * (issue 195, S19): `forbidden` is the server saying no, `unavailable` is the
 * server not saying anything. Collapsing the second into the first would tell a
 * user with the role that they do not have it.
 */
export type AccessVerdict =
  | { state: 'allowed' }
  | { state: 'forbidden' }
  | { state: 'unavailable'; error: unknown };

/** ONLY the module API's own refusal is a permission verdict; a 500 or a timeout says nothing about the caller. */
function verdictOf(error: unknown): AccessVerdict {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403 ? { state: 'forbidden' } : { state: 'unavailable', error };
}

/** May the caller import into this module? One GET on the module's descriptor, never cached here. */
export async function probeImportAccess(key: ImportModuleKey): Promise<AccessVerdict> {
  const module = IMPORT_MODULES[key];
  try {
    await module.api.get(`${module.importBase}/descriptor`, { suppressGlobalError: true } as never);
    return { state: 'allowed' };
  } catch (error) {
    return verdictOf(error);
  }
}

/** May the caller create exports in this module? The export list sits behind `data_export.export`; its first page rides along. */
export async function probeExportAccess(key: ImportModuleKey): Promise<{ verdict: AccessVerdict; items: any[] }> {
  const module = IMPORT_MODULES[key];
  try {
    const { data } = await module.api.get(`${module.exportBase}/jobs`, {
      params: { limit: 10 },
      suppressGlobalError: true,
    } as never);
    return { verdict: { state: 'allowed' }, items: Array.isArray(data?.items) ? data.items : [] };
  } catch (error) {
    return { verdict: verdictOf(error), items: [] };
  }
}

/**
 * Does the caller hold "Zugang verwalten" (`person.access.manage`) in People?
 * People's `GET /access/me` lists what the guard let through for the caller's
 * active role (the workspace admin's derived set included). `unknown` when People
 * does not answer: the hub then shows the role to ask rather than a direct link
 * it cannot vouch for.
 */
export async function probeCanManagePeopleAccess(): Promise<'yes' | 'no' | 'unknown'> {
  try {
    const { data } = await peopleApi.get('/access/me', { suppressGlobalError: true } as never);
    const permissions: unknown = data?.permissions;
    return Array.isArray(permissions) && permissions.includes('person.access.manage') ? 'yes' : 'no';
  } catch (error) {
    const verdict = verdictOf(error);
    return verdict.state === 'forbidden' ? 'no' : 'unknown';
  }
}

/** The tenant facts the licence decision reads (the tenants store's). */
export interface LicensedTenant {
  modules?: Array<{ moduleKey: string; status: string }>;
}

/**
 * The import modules this workspace has booked (active or trial). user-api speaks
 * its own module namespace (`time_tracking`, …), translated to the Lumi key first,
 * or Timecard would silently disappear. Empty while the module list is unknown.
 */
export function licensedImportModules(tenant: LicensedTenant | null | undefined): ImportModuleKey[] {
  const booked = new Set(
    (tenant?.modules ?? [])
      .filter((module) => module.status === 'active' || module.status === 'trial')
      .map((module) => USER_API_TO_LUMI[module.moduleKey] ?? module.moduleKey),
  );
  return IMPORT_MODULE_KEYS.filter((key) => booked.has(key));
}

// ── The sidebar's question: does she hold "Daten importieren" anywhere? ──
// (the kept answers live in ./dataImportAccessCache so the stores can forget them.)

/**
 * True when at least one of the licensed modules answers its descriptor with a 2xx.
 * Concurrent asks share one request. Never throws.
 */
export function probeAnyImportAccess(
  userId: string | null | undefined,
  tenantSlug: string | null | undefined,
  moduleKeys: readonly ImportModuleKey[],
): Promise<boolean> {
  if (!userId || !tenantSlug || moduleKeys.length === 0) return Promise.resolve(false);
  const key = anyAccessKey(userId, tenantSlug);
  const known = anyAccessAnswers.get(key);
  if (known !== undefined) return Promise.resolve(known);
  const pending = anyAccessInflight.get(key);
  if (pending) return pending;
  const startedIn = anyAccessGeneration();
  const request = Promise.all(moduleKeys.map((moduleKey) => probeImportAccess(moduleKey))).then((verdicts) => {
    const granted = verdicts.some((verdict) => verdict.state === 'allowed');
    const settled = granted || verdicts.every((verdict) => verdict.state === 'forbidden');
    if (startedIn === anyAccessGeneration()) {
      anyAccessInflight.delete(key);
      if (settled) anyAccessAnswers.set(key, granted);
    }
    return granted;
  });
  anyAccessInflight.set(key, request);
  return request;
}
